export type DecoderKind = "native" | "zxing" | "none";
export type DecodeResult = { text: string; format: string };
export type Decoder = { kind: DecoderKind; decode(source: ImageBitmapSource | ImageData): Promise<DecodeResult | null> };
export type ZxingLike = { readBarcodes(source: ImageData | Blob, opts?: unknown): Promise<Array<{ text: string; format: string }>> };

export const FORMATS_NATIVE = ["ean_13", "upc_a", "upc_e", "ean_8", "code_128", "qr_code"];
export const FORMATS_ZXING = ["EAN-13", "UPC-A", "UPC-E", "EAN-8", "Code128", "QRCode"];

// jsdom (used in tests) defines neither ImageData nor createImageBitmap, so an
// `instanceof ImageData` check throws ReferenceError under vitest. Duck-type
// instead — this also happily accepts the plain-object fakes tests pass in.
function isImageData(src: unknown): src is ImageData {
  return typeof src === "object" && src !== null && "data" in src && "width" in src && "height" in src;
}

async function defaultLoadZxing(): Promise<ZxingLike> {
  const mod = await import("zxing-wasm/reader");
  const wasmUrl = (await import("zxing-wasm/reader/zxing_reader.wasm?url")).default;
  mod.prepareZXingModule({ overrides: { locateFile: () => wasmUrl }, fireImmediately: true });
  return { readBarcodes: (src, opts) => mod.readBarcodes(src as never, opts as never) as never };
}

export async function createDecoder(opts: { loadZxing?: () => Promise<ZxingLike> } = {}): Promise<Decoder> {
  const BD = window.BarcodeDetector;
  if (BD) {
    const supported = await BD.getSupportedFormats();
    if (supported.includes("ean_13") || supported.includes("upc_a")) {
      const det = new BD({ formats: FORMATS_NATIVE });
      return {
        kind: "native",
        decode: async (src) => {
          const first = (await det.detect(src))[0];
          return first ? { text: first.rawValue, format: first.format } : null;
        },
      };
    }
    // the native detector exists but doesn't support the formats we need — fall through to zxing
  }
  try {
    const zx = await (opts.loadZxing ?? defaultLoadZxing)();
    return {
      kind: "zxing",
      decode: async (src) => {
        const data = isImageData(src) ? src : await toImageData(src);
        if (!data) return null;
        const r = await zx.readBarcodes(data, { formats: FORMATS_ZXING, tryHarder: true, maxNumberOfSymbols: 1 });
        const first = r[0];
        return first ? { text: first.text, format: first.format } : null;
      },
    };
  } catch {
    return { kind: "none", decode: async () => null };
  }
}

export function acceptScan(history: string[], value: string, needed = 2): { history: string[]; accepted: boolean } {
  const next = history.length && history[history.length - 1] === value ? [...history, value] : [value];
  return { history: next.slice(-needed), accepted: next.length >= needed };
}

async function toImageData(src: ImageBitmapSource, maxSide = 1600): Promise<ImageData | null> {
  const bmp = await createImageBitmap(src);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

export async function decodeImageFile(file: Blob, decoder: Decoder): Promise<DecodeResult | null> {
  if (decoder.kind === "native") return decoder.decode(file as ImageBitmapSource);
  const data = await toImageData(file as ImageBitmapSource);
  return data ? decoder.decode(data) : null;
}
